import React from 'react';

const KOLEGA_HOME_URL = 'https://kolega.dev';

export interface FooterLink {
  label: string;
  href: string;
}

const defaultFooterLinks: FooterLink[] = [
  { label: 'Terms of Service', href: 'https://kolega.dev/terms' },
  { label: 'Privacy Policy', href: 'https://kolega.dev/privacy' },
  { label: 'Responsible Disclosure', href: 'https://kolega.dev/responsible-disclosure' },
  { label: 'Status', href: 'https://status.kolega.dev' },
];

interface PublicBrandShellProps {
  children: React.ReactNode;
  mainClassName?: string;
  // Override the footer links (e.g. in-app /pricing, /terms, /privacy, /refund, /contact)
  // so public storefront pages keep visitors on this domain. Defaults to kolega.dev links.
  footerLinks?: FooterLink[];
}

export const PublicBrandHeader: React.FC = () => (
  <header className="border-b border-[#2A2A2A] bg-black">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      <div className="flex items-center justify-between gap-4">
        <a
          href={KOLEGA_HOME_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex min-w-0 items-center gap-3 text-white transition-opacity hover:opacity-80"
        >
          <img src="/logos/kolega.svg" alt="Kolega.dev logo" className="h-8 w-8 flex-shrink-0" />
          <span className="truncate text-xl font-bold">Kolega.dev</span>
        </a>
        <a
          href={KOLEGA_HOME_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-md px-3 py-2 text-sm font-medium text-[#B3B3B3] transition-colors hover:bg-[#2A2A2A] hover:text-[#ADDD30]"
        >
          Home
        </a>
      </div>
    </div>
  </header>
);

export const PublicBrandFooter: React.FC<{ links?: FooterLink[] }> = ({ links = defaultFooterLinks }) => (
  <footer role="contentinfo" className="border-t border-[#2A2A2A] bg-black py-8">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center text-[#666666]">
        <a
          href={KOLEGA_HOME_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mb-4 inline-flex items-center justify-center gap-3 text-white transition-opacity hover:opacity-80"
        >
          <img src="/logos/kolega.svg" alt="Kolega.dev logo" className="h-6 w-6" />
          <span className="font-bold">Kolega.dev</span>
        </a>
        <p className="mb-3 text-sm">
          &copy; {new Date().getFullYear()} Kolega.dev. The Code Remediation Engine.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2 text-xs sm:gap-4">
          {links.map((link) => {
            const isExternal = /^https?:\/\//.test(link.href);
            return (
              <a
                key={link.label}
                href={link.href}
                {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                className="px-1 py-2 text-[#808080] underline transition-colors hover:text-[#ADDD30]"
              >
                {link.label}
              </a>
            );
          })}
        </div>
      </div>
    </div>
  </footer>
);

export const PublicBrandShell: React.FC<PublicBrandShellProps> = ({ children, mainClassName = '', footerLinks }) => (
  <div className="flex min-h-screen flex-col bg-black text-white">
    <PublicBrandHeader />
    <main className={`flex-1 ${mainClassName}`}>{children}</main>
    <PublicBrandFooter links={footerLinks} />
  </div>
);
